// The Radar on ?live=1 (Track 2 demo push, item 3): her shape and its scale from her league's
// Stats file — the shared group (`live/group-players.ts`), as Ranking reads it.
//
// The decided defaults (Prateek, 2026-10-15; radar.md "Decisions"):
// - Q-009 (Ben): a template means minutes AT that position, on both sides. With Full Back picked,
//   her shape is her full-back minutes only (the file's "group" row), and the scale is everyone's
//   full-back minutes (a centre back's 3 games there count, with those games only).
// - Q-011 A (Ben, 2026-10-26): a template spoke's scale is Catalyst's own fixed numbers
//   (live/catalyst-radar-scale.ts). A custom spoke (any other stat) keeps the scale worked out from
//   the players (B, what Ben's prototype does): lowest to highest value at that position, no
//   minutes floor (rules.md: no minimum minutes by default).
// - The group = her league's file for the season shown (Q-010 meanwhile), so one gender; movers
//   who left this league are out of the scale (Q-007), as on the Stats tile.
// - The goalkeeper template's `claim_success_perc` (wrong, then removed in v1.7.0) becomes
//   "Claimable Claim Success %" only, as Catalyst (Ben, 2026-10-26: "1a"; Q-015 B's second spoke,
//   CCAX %, stays on the Stats tile and can still be added as a custom spoke). CCAX is signed
//   (−100%..+100%): as a custom spoke it runs min → max, the lowest (often negative) value at the
//   centre. The swap happens before the axes get here (hooks/useRadarAxes.ts). No claim data → "—"
//   (`blankNoClaimData`, via `groupRowsOf`). A hidden key that still arrives (none today) shows
//   "—" on its scale rather than a made-up 0–0.

import type { StatsFile } from "@catbento/shared";
import type { PlayerId } from "../data/players.js";
import { radarRange, type RadarAxis, type RadarRange } from "../modules/radar-view.js";
import { catalystRange } from "./catalyst-radar-scale.js";
import { groupPlayers, mainPositions, type GroupFile, type GroupPlayer } from "./group-players.js";
import { isHiddenLive } from "./stats.js";
import { groupRowsOf } from "./stats-files.js";

/** Not a file column: every row carries her minutes there as a fixed column (`groupRowsOf`). */
const MINUTES = "minutes";

/** The file columns the radar asks for: its axes, minus Minutes. */
function fileKeys(axes: readonly RadarAxis[]): string[] {
  return axes.map((a) => a.key).filter((k) => k !== MINUTES);
}

/** The template a fresh Radar opens on: the position she played most in this file, or null if
 *  she has no minutes in it (then the radar says there's no data). Pure. */
export function herTemplate(file: StatsFile, playerId: PlayerId): string | null {
  return mainPositions(file).get(playerId) ?? null;
}

/**
 * Her values on the axes: her WHOLE season in this file, whatever position she played (Ben,
 * 2026-10-18 — replaces Q-009's "only her minutes at the template's position": "it doesn't matter
 * what position the guy plays… we are plotting his numbers for all the axes a midfielder radar
 * has"). The template picks the axes (and the scale's players); null = she isn't in this file.
 * `template` stays in the signature for the callers, which pass it for the scale. Pure.
 */
export function herValuesAt(
  file: StatsFile,
  playerId: PlayerId,
  _template: string,
  axes: readonly RadarAxis[],
): Record<string, number | null> | null {
  const row = groupRowsOf(file, { kind: "total" }, fileKeys(axes)).find((r) => r.playerId === playerId);
  return row ? row.stats : null;
}

/** The scale's players: everyone's minutes at `template` in this file (Q-009), in the snapshot's
 *  shape so Ben's `radarRange` / Distribution read it unchanged. Pure. */
export function templatePopulation(
  group: GroupFile,
  template: string,
  axes: readonly RadarAxis[],
  leagueLabel: string,
): GroupPlayer[] {
  return Object.values(groupPlayers(group, "position", fileKeys(axes), leagueLabel)).filter((p) => p.positionTemplate === template);
}

/** Each axis's scale: Catalyst's fixed numbers for a template spoke (Q-011 A), else worked out
 *  over the population (Ben's `radarRange`); a hidden metric → NaN, which every label shows as
 *  "—". Pure. */
export function liveRadarRanges(population: readonly GroupPlayer[], template: string, axes: readonly RadarAxis[]): Map<string, RadarRange> {
  const map = new Map<string, RadarRange>();
  for (const a of axes) {
    map.set(a.key, isHiddenLive(a.key) ? { min: NaN, max: NaN } : (catalystRange(template, a.key, a.outputType) ?? radarRange(population, template, a.key)));
  }
  return map;
}

/**
 * A merged Radar (T2-16b): each player's values from HER OWN league's file (`groups[i]`, null =
 * no file → no values), all on ONE scale — the first (the tile's own) player's file at the
 * template, as the prototype scales every shape on the tile's own population. `scaleLabel` = that
 * file's league label. Pure.
 */
export function radarComparison(
  groups: readonly (GroupFile | null)[],
  ids: readonly PlayerId[],
  scaleLabel: string | null,
  template: string,
  axes: readonly RadarAxis[],
): { valuesList: Record<string, number | null>[]; ranges: Map<string, RadarRange>; population: GroupPlayer[] } {
  const valuesList = ids.map((id, i) => (groups[i] ? herValuesAt(groups[i]!.file, id, template, axes) : null) ?? {});
  const scale = groups[0];
  const population = scale && scaleLabel !== null ? templatePopulation(scale, template, axes, scaleLabel) : [];
  return { valuesList, ranges: liveRadarRanges(population, template, axes), population };
}

/** Why a player has no shape: with her whole season plotted (above), only that she isn't in this
 *  league season's data. Shown instead of a bare "no data" (Ben, 2026-10-18). */
export function whyNoShape(_file: StatsFile, _playerId: PlayerId, _template: string, name: string, leagueSeason: string): string {
  return `${name} isn't in the ${leagueSeason} data.`;
}
